import throttle from 'lodash/throttle';
import raf from 'raf';
import { intBetween, getCoords } from './util.js';

export default class ScrollingMonitor {
  constructor(dragDropManager, container, options) {
    this.dragDropManager = dragDropManager;
    this.container = container;
    this.options = options;

    this.scaleX = 0;
    this.scaleY = 0;
    this.frame = null;

    this.attached = false;
    this.dragging = false;
  }

  start() {
    window.addEventListener('dragover', this.handleEvent);
    // touchmove events don't seem to work across siblings, so we unfortunately
    // have to attach the listeners to the body
    window.document.body.addEventListener('touchmove', this.handleEvent);

    this.clearMonitorSubscription = this.dragDropManager
      .getMonitor()
      .subscribeToStateChange(() => this.handleMonitorChange());
  }

  stop() {
    window.removeEventListener('dragover', this.handleEvent);
    window.document.body.removeEventListener('touchmove', this.handleEvent);
    this.clearMonitorSubscription();
    this.stopScrolling();
  }

  handleEvent = (evt) => {
    if (this.dragging && !this.attached) {
      this.attach();
      this.updateScrolling(evt);
    }
  };

  handleMonitorChange() {
    const isDragging = this.dragDropManager.getMonitor().isDragging();

    if (!this.dragging && isDragging) {
      this.dragging = true;
    } else if (this.dragging && !isDragging) {
      this.dragging = false;
      this.stopScrolling();
    }
  }

  attach() {
    this.attached = true;
    window.document.body.addEventListener('dragover', this.updateScrolling);
    window.document.body.addEventListener('touchmove', this.updateScrolling);
  }

  detach() {
    this.attached = false;
    window.document.body.removeEventListener('dragover', this.updateScrolling);
    window.document.body.removeEventListener('touchmove', this.updateScrolling);
  }

  // Update scaleX and scaleY every 100ms or so
  // and start scrolling if necessary
  updateScrolling = throttle(
    (evt) => {
      const { innerWidth: w, innerHeight: h } = window;
      const box = { x: 0, y: 0, w, h };
      const coords = getCoords(evt);

      // calculate strength
      this.scaleX = this.options.horizontalStrength(box, coords);
      this.scaleY = this.options.verticalStrength(box, coords);

      // start scrolling if we need to
      if (!this.frame && (this.scaleX || this.scaleY)) {
        this.startScrolling();
      }
    },
    100,
    { trailing: false },
  );

  startScrolling() {
    let i = 0;
    const tick = () => {
      const { scaleX, scaleY } = this;
      const { strengthMultiplier, onScrollChange } = this.options;

      // stop scrolling if there's nothing to do
      if (strengthMultiplier === 0 || scaleX + scaleY === 0) {
        this.stopScrolling();
        return;
      }

      // there's a bug in safari where it seems like we can't get
      // mousemove events from a container that also emits a scroll
      // event that same frame. So we double the strengthMultiplier and only adjust
      // the scroll position at 30fps
      if (i++ % 2) {
        const { scrollX, scrollY, innerWidth, innerHeight } = window;
        const { scrollWidth, scrollHeight } = window.document.documentElement;

        const newLeft = scaleX
          ? intBetween(0, scrollWidth - innerWidth, scrollX + scaleX * strengthMultiplier)
          : scrollX;

        const newTop = scaleY
          ? intBetween(0, scrollHeight - innerHeight, scrollY + scaleY * strengthMultiplier)
          : scrollY;

        window.scroll(newLeft, newTop);

        onScrollChange(newLeft, newTop);
      }
      this.frame = raf(tick);
    };

    tick();
  }

  stopScrolling() {
    this.detach();
    this.scaleX = 0;
    this.scaleY = 0;

    if (this.frame) {
      raf.cancel(this.frame);
      this.frame = null;
    }
  }
}
